import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import {
  Sparkles,
  Calendar,
  Briefcase,
  Home,
  Users,
  Loader2,
  ChevronRight,
  MapPin,
  Building
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const AIRecommendations = () => {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchRecommendations();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/api/ai/recommendations`);
      setRecommendations(res.data);
    } catch (error) {
      console.error('Failed to fetch AI recommendations:', error);
      setError('Could not load personalized recommendations');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-bold text-lg">Personalized for You</h2>
            <p className="text-sm text-muted-foreground">Loading recommendations...</p>
          </div>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !recommendations) {
    return null;
  }

  const hasContent = 
    (recommendations.recommended_events?.length > 0) ||
    (recommendations.recommended_jobs?.length > 0) ||
    (recommendations.recommended_properties?.length > 0) ||
    (recommendations.recommended_connections?.length > 0);

  if (!hasContent) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-bold text-lg">Personalized for You</h2>
          <p className="text-sm text-muted-foreground">AI-powered recommendations based on your profile</p>
        </div>
      </div>

      {/* Events for You */}
      {recommendations.recommended_events?.length > 0 && (
        <div className="bg-gradient-to-br from-primary/5 via-purple-50 to-accent/10 rounded-2xl p-5 md:p-6 border-2 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="font-bold">Events for You</span>
            </div>
            <Link to="/events" className="text-sm text-primary hover:underline flex items-center gap-1">
              See all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommendations.recommended_events.map((event, idx) => (
              <Link 
                key={idx} 
                to={`/events/${event.event_id}`}
                className="bg-white rounded-xl p-4 border hover:border-primary/50 hover:shadow-md transition-all"
              >
                <h3 className="font-bold text-sm mb-1 line-clamp-1">{event.title}</h3>
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {event.city}
                </p>
                {event.ai_reason && (
                  <p className="text-xs text-primary/80 bg-primary/5 rounded px-2 py-1">
                    {event.ai_reason}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Expats to Connect With */}
      {recommendations.recommended_connections?.length > 0 && (
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-5 md:p-6 border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              <span className="font-bold">Expats to Connect With</span>
            </div>
            <Link 
              to={`/find-expats?city=${encodeURIComponent(user?.browsing_city || user?.residence_city || '')}&country=${encodeURIComponent(user?.browsing_country || user?.residence_country || '')}&autoSearch=true`} 
              className="text-sm text-green-600 hover:underline flex items-center gap-1"
            >
              Find more <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommendations.recommended_connections.map((person, idx) => (
              <Link 
                key={idx} 
                to={`/profile/${person.user_id}`}
                className="bg-white rounded-xl p-4 border hover:border-green-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">{person.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {person.nationality} in {person.residence_city}
                    </p>
                  </div>
                </div>
                {person.ai_reason && (
                  <p className="text-xs text-green-600/80 bg-green-50 rounded px-2 py-1 mt-2">
                    {person.ai_reason}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Housing for You */}
      {recommendations.recommended_properties?.length > 0 && (
        <div className="bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 rounded-2xl p-5 md:p-6 border-2 border-teal-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Home className="w-5 h-5 text-teal-600" />
              <span className="font-bold">Housing for You</span>
            </div>
            <Link to="/housing" className="text-sm text-teal-600 hover:underline flex items-center gap-1">
              See all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommendations.recommended_properties.map((property, idx) => (
              <Link 
                key={idx} 
                to={`/housing/${property.property_id}`}
                className="bg-white rounded-xl p-4 border hover:border-teal-400 hover:shadow-md transition-all"
              >
                <h3 className="font-bold text-sm mb-1 line-clamp-1">{property.title}</h3>
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {property.city} • {property.bedrooms} bed
                </p>
                <p className="text-sm font-bold text-teal-600">
                  ${property.price?.toLocaleString()}
                </p>
                {property.ai_reason && (
                  <p className="text-xs text-teal-600/80 bg-teal-50 rounded px-2 py-1 mt-2">
                    {property.ai_reason}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Career Opportunities */}
      {recommendations.recommended_jobs?.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 rounded-2xl p-5 md:p-6 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-600" />
              <span className="font-bold">Career Opportunities</span>
            </div>
            <Link to="/jobs" className="text-sm text-purple-600 hover:underline flex items-center gap-1">
              See all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recommendations.recommended_jobs.map((job, idx) => (
              <Link 
                key={idx} 
                to={`/jobs/${job.job_id}`}
                className="bg-white rounded-xl p-4 border hover:border-purple-400 hover:shadow-md transition-all"
              >
                <h3 className="font-bold text-sm mb-1 line-clamp-1">{job.title}</h3>
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  {job.company_name} • {job.city}
                </p>
                {job.ai_reason && (
                  <p className="text-xs text-purple-600/80 bg-purple-50 rounded px-2 py-1">
                    {job.ai_reason}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRecommendations;
